"use client";

import { useEffect, useState } from "react";

type MarketingAvatar = {
  imageUrl: string;
};

type MarketingAvatarsResponse = {
  avatars: MarketingAvatar[];
  totalCount: number;
};

const MAX_REMAINING_LABEL = 99;

function isMarketingAvatarsResponse(value: unknown): value is MarketingAvatarsResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const candidate = value as { avatars?: unknown; totalCount?: unknown };
  return (
    Array.isArray(candidate.avatars) &&
    candidate.avatars.every(
      (avatar) =>
        typeof avatar === "object" &&
        avatar !== null &&
        typeof (avatar as { imageUrl?: unknown }).imageUrl === "string",
    ) &&
    typeof candidate.totalCount === "number"
  );
}

export function HeroV2AvatarStack() {
  const [avatarData, setAvatarData] = useState<MarketingAvatarsResponse | null>(null);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    async function loadAvatars() {
      try {
        const response = await fetch("/api/marketing/clerk-avatars", {
          signal: controller.signal,
        });
        if (!response.ok) {
          return;
        }

        const payload: unknown = await response.json();
        if (isMounted && isMarketingAvatarsResponse(payload)) {
          setAvatarData(payload);
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
      }
    }

    void loadAvatars();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  const avatars = avatarData?.avatars ?? [];
  if (avatars.length === 0) {
    return null;
  }

  const remainingCount = Math.max((avatarData?.totalCount ?? avatars.length) - avatars.length, 0);
  const remainingLabel =
    remainingCount > 0 ? `+${Math.min(remainingCount, MAX_REMAINING_LABEL)}` : null;

  return (
    <div className="mb-3 flex justify-center" aria-label="Recent Darsi teacher profile photos">
      <div className="flex -space-x-3">
        {avatars.map((avatar, index) => (
          <span
            key={`${avatar.imageUrl}-${index}`}
            aria-label={`Darsi teacher profile ${index + 1}`}
            className="block h-[35px] w-[35px] shrink-0 rounded-full border-[2px] border-white bg-slate-100 bg-cover bg-center sm:h-[45px] sm:w-[45px]"
            role="img"
            style={{ backgroundImage: `url(${avatar.imageUrl})` }}
          />
        ))}

        {remainingLabel ? (
          <span
            aria-label={`${remainingCount} more Darsi teacher accounts`}
            className="flex h-[35px] w-[35px] shrink-0 items-center justify-center rounded-full border-[2px] border-white bg-slate-950 text-sm font-black text-white sm:h-[45px] sm:w-[45px] sm:text-base"
          >
            {remainingLabel}
          </span>
        ) : null}
      </div>
    </div>
  );
}
